import type { KeyboardEvent } from 'react';
import { PULAR_NO_ENTER } from '../lib/teclado';
import css from './Segmentos.module.css';

const letraDe = (rotulo: string) => rotulo.charAt(0).toLocaleLowerCase('pt-BR');

/**
 * Rádios com cara de botões colados (o rádio de verdade fica por baixo, para teclado e leitor de tela).
 * No Enter do formulário o grupo é uma parada só: entra no escolhido e sai para o campo seguinte.
 */
export function Segmentos<T extends string>({
  rotulo,
  nome,
  opcoes,
  valor,
  aoMudar,
  pequeno,
  atalhos,
  invalido,
  descritoPor,
}: {
  rotulo: string;
  nome: string;
  opcoes: { valor: T; rotulo: string }[];
  /** null = nenhuma escolhida ainda. */
  valor: T | null;
  aoMudar: (valor: T) => void;
  pequeno?: boolean;
  /** Cada opção atende pela 1ª letra do rótulo (sublinhada) com o foco no grupo: P, D, C, O. */
  atalhos?: boolean;
  invalido?: boolean;
  /** id da mensagem de erro do grupo. */
  descritoPor?: string | undefined;
}) {
  const marcado = opcoes.some((o) => o.valor === valor) ? valor : null;

  function teclar(e: KeyboardEvent<HTMLDivElement>) {
    if (e.ctrlKey || e.metaKey || e.altKey || e.nativeEvent.isComposing || e.key.length !== 1)
      return;
    const opcao = opcoes.find((o) => letraDe(o.rotulo) === e.key.toLocaleLowerCase('pt-BR'));
    if (!opcao) return;
    e.preventDefault();
    aoMudar(opcao.valor);
    // O foco vai junto para a escolhida: o Enter seguinte sai do grupo a partir dela.
    const radios = Array.from(e.currentTarget.querySelectorAll('input'));
    radios.find((r) => r.value === opcao.valor)?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={rotulo}
      aria-invalid={invalido || undefined}
      aria-describedby={descritoPor}
      className={pequeno ? css.pequenos : css.segmentos}
      onKeyDown={atalhos ? teclar : undefined}
    >
      {opcoes.map((o, i) => (
        <label key={o.valor} className={css.segmento}>
          <input
            type="radio"
            name={nome}
            value={o.valor}
            checked={valor === o.valor}
            onChange={() => aoMudar(o.valor)}
            aria-keyshortcuts={atalhos ? o.rotulo.charAt(0).toUpperCase() : undefined}
            {...((marcado === null ? i > 0 : o.valor !== marcado) ? PULAR_NO_ENTER : {})}
          />
          {atalhos ? (
            <span>
              <u className={css.letra}>{o.rotulo.charAt(0)}</u>
              {o.rotulo.slice(1)}
            </span>
          ) : (
            o.rotulo
          )}
        </label>
      ))}
    </div>
  );
}
